"use client";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../app/lib/supabase/client";
import AuthPopup from "../../components/auth/AuthPopup";
import { profileQueryKey, useProfile } from "../../app/hooks/useProfile";
import { useState, useEffect, Suspense } from "react";

const DASHBOARD_HREF = "/dashboard/resume";

function HeaderImpl() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);

  const [authOpen, setAuthOpen] = useState(false);
  const [authInitialMode, setAuthInitialMode] = useState("signin");
  const [postLoginRedirect, setPostLoginRedirect] = useState(null);
  const queryClient = useQueryClient();
  const { data, isError, error } = useProfile();
  const profileUser = data?.user ?? null;
  const [sessionUser, setSessionUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const isLoggedIn = Boolean(sessionUser ?? profileUser);
  const menu = ["Home", "Articles", "Companies", "Universities", "Contact"];

  useEffect(() => {
    const signin = searchParams.get("signin") === "1";
    const register = searchParams.get("register") === "1";
    if (!signin && !register) return;

    const next = searchParams.get("next");
    setPostLoginRedirect(next && next.startsWith("/") ? next : null);
    setAuthInitialMode(register ? "signup" : "signin");
    setAuthOpen(true);

    const p = new URLSearchParams(searchParams.toString());
    p.delete("signin");
    p.delete("register");
    p.delete("next");
    const qs = p.toString();
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    router.replace(`${pathname}${qs ? `?${qs}` : ""}${hash}`, { scroll: false });
  }, [searchParams, pathname, router]);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!cancelled) {
        setSessionUser(session?.user ?? null);
        setAuthReady(true);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSessionUser(session?.user ?? null);
      setAuthReady(true);
      if (event === "SIGNED_OUT") {
        queryClient.setQueryData(profileQueryKey, { user: null, profile: null });
      }
      queryClient.invalidateQueries({ queryKey: profileQueryKey });
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const handleSignOut = async () => {
    setSessionUser(null);
    queryClient.setQueryData(profileQueryKey, { user: null, profile: null });
    queryClient.removeQueries({ queryKey: profileQueryKey });
    await supabase.auth.signOut({ scope: "local" });
    router.refresh();
  };

  useEffect(() => {
    const trackFirstVisit = () => {
      // Проверяем, есть ли уже запись о первом визите
      const hasFirstPage = localStorage.getItem("first_page");
      if (!hasFirstPage) {
        // Сохраняем первую страницу
        localStorage.setItem("first_page", window.location.href);
        // Сохраняем реферер (если есть)
        localStorage.setItem("first_referrer", document.referrer || "");
      }
    };

    trackFirstVisit();
  }, []); // Пустой массив — только при монтировании

  return (
    <div className="relative">
      {/* SVG фон */}
      <div className="fixed inset-0 -z-10 w-full h-full overflow-hidden">
        <svg className="w-full h-full" viewBox="0 0 1440 870" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_9810_205843)">
            <g clipPath="url(#clip1_9810_205843)">
              {/* основной фон с полной непрозрачностью */}
              <rect width="1440" height="870" fill="url(#paint0_linear_9810_205843)" fillOpacity="0.3" />
            </g>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M248.493 0H193.317L210.837 60.017L248.493 0ZM128.485 0H192.243L210.478 62.4666L249.671 0H318.954L188.464 207.979L128.485 0ZM127.412 0H9.65819L0 15.3935V137.805L79.5127 11.0752L165.827 308.485L490.699 228.213L493.085 229.481L208.242 683.473L136.551 434.882L197.866 419.618L230.98 533.053L364.674 319.967L181.599 365.123L181.596 365.123L119.803 380.044L119.8 380.045L0 409.813V457.13L40.8024 447.048L43.1884 448.316L0 517.151V639.562L98.9873 481.793L185.301 779.202L510.174 698.931L512.56 700.199L406.023 870H482.827L568.359 733.677L607.923 870H658.766L605.923 686.764L667.238 671.501L700.351 784.935L834.046 571.849L650.971 617.005L650.968 617.006L589.175 631.927L589.172 631.927L267.188 711.934L548.885 262.958L635.198 560.367L960.071 480.096L962.457 481.364L718.619 870H795.423L1018.26 514.842L1104.57 812.251L1429.44 731.979L1431.83 733.248L1346.03 870H1422.84L1440 842.647V679.499L1185.79 742.666L1440 337.495V203.942L1166.31 271.948L1336.94 0H1260.14L1107.37 243.485L1037.15 0H777.689L696.943 20.0641L709.531 0H589.711L615.056 87.3323L939.929 7.06061L942.315 8.3291L657.472 462.32L585.78 213.729L647.095 198.466L680.209 311.9L813.903 98.8145L630.828 143.97L630.826 143.971L569.033 158.891L569.029 158.892L247.046 238.899L396.936 0H320.132L188.1 210.437L127.412 0ZM398.113 0L249.182 237.371L568.802 157.952L568.805 157.951L630.599 143.03L630.601 143.029L816.037 97.2913L679.85 314.35L646.365 199.645L587.05 214.411L657.836 459.862L940.9 8.70685L939.758 8.09979L614.324 88.5101L588.637 0H523.68L553.294 101.898L368.2 147.599L460.713 0H398.113ZM461.89 0L370.333 146.075L552.021 101.215L522.606 0H461.89ZM710.709 0L699.079 18.5358L773.676 0H710.709ZM1188.5 0H1102.94L1130.11 93.0653L1188.5 0ZM1101.86 0L1129.75 95.5149L1189.67 0H1258.96L1107.73 241.027L1038.22 0H1101.86ZM1338.12 0L1168.45 270.42L1440 202.945V142.986L1287.47 180.648L1400.7 0H1338.12ZM1401.87 0H1432.27L1440 26.5988V141.989L1289.6 179.124L1401.87 0ZM1440 22.9041L1433.34 0H1440V22.9041ZM1440 339.372L1187.92 741.137L1440 678.502V618.512L1306.94 651.365L1440 439.08V339.372ZM1440 440.959L1309.08 649.842L1440 617.515V440.959ZM1440 844.525L1424.02 870H1440V844.525ZM1344.85 870H1275.57L1305.55 822.21L1120.12 867.948L1120.11 867.949L1111.62 870H867.911L1042.81 826.817L993.619 657.567L993.792 657.292L992.223 656.457L992.513 657.454L859.292 870H796.601L1017.89 517.295L1103.84 813.429L1429.27 733.019L1430.41 733.626L1344.85 870ZM863.874 870H860.47L992.886 658.738L1041.54 826.134L863.874 870ZM1115.74 870L1120.34 868.89L1120.34 868.889L1303.42 823.733L1274.39 870H1115.74ZM717.441 870L961.042 481.742L959.9 481.135L634.467 561.545L548.523 265.411L269.325 710.406L588.944 630.987L588.948 630.986L650.741 616.065L650.744 616.064L836.179 570.326L699.992 787.385L666.508 672.68L607.193 687.446L659.839 870H717.441ZM606.849 870L567.997 736.13L484.005 870H606.849ZM404.846 870H335.562L386.282 789.161L200.847 834.899L200.844 834.9L139.05 849.821L139.047 849.822L57.8396 870H0V824.27L123.539 793.767L74.3506 624.518L74.5228 624.243L72.9542 623.408L73.2439 624.405L0 741.262V641.439L98.6257 484.246L184.569 780.38L510.003 699.97L511.145 700.577L404.846 870ZM0 823.273V743.14L73.6169 625.688L122.267 793.085L0 823.273ZM61.8534 870L139.275 850.762L139.278 850.761L201.071 835.841L201.073 835.84L384.148 790.684L334.384 870H61.8534ZM0 515.274L41.7734 448.694L40.6316 448.087L0 458.127V515.274ZM0 408.815L119.572 379.104L119.576 379.103L181.369 364.182L181.372 364.182L366.807 318.444L230.62 535.502L197.136 420.797L137.821 435.563L208.607 681.015L491.67 229.859L490.529 229.252L165.095 309.662L79.1511 13.5288L0 139.682V239.473L53.7694 153.687L53.4797 152.691L55.0482 153.526L54.876 153.801L104.064 323.05L0 348.744V408.815ZM0 347.747L102.792 322.367L54.1425 154.971L0 241.352V347.747ZM8.48029 0H0V13.5161L8.48029 0ZM1411.69 260.213L1126.84 714.204L1055.15 465.613L1116.47 450.35L1149.58 563.784L1283.27 350.698L1100.2 395.854L1100.2 395.855L1038.4 410.775L1038.4 410.776L716.417 490.783L998.114 41.8066L1084.43 339.216L1409.3 258.944L1411.69 260.213ZM1409.13 259.984L1083.7 340.394L997.752 44.2603L718.554 489.255L1038.17 409.835L1038.18 409.834L1099.97 394.914L1099.97 394.913L1285.41 349.175L1149.22 566.234L1115.74 451.529L1056.42 466.294L1127.21 711.746L1410.27 260.591L1409.13 259.984ZM972.37 184.419L972.081 183.422L973.649 184.257L973.477 184.532L1022.67 353.781L837.572 399.483L972.37 184.419ZM972.743 185.702L1021.39 353.099L839.704 397.959L972.743 185.702ZM523.141 405.57L522.851 404.574L524.42 405.409L524.248 405.683L573.436 574.933L388.342 620.634L523.141 405.57ZM523.514 406.854L572.164 574.25L390.475 619.111L523.514 406.854Z"
              fill="#FAFCFF"
            />
            <rect y="636" width="1440" height="234" fill="url(#paint1_linear_9810_205843)" />
          </g>
          <defs>
            <linearGradient id="paint0_linear_9810_205843" x1="0.000118291" y1="435" x2="817.267" y2="-278.4" gradientUnits="userSpaceOnUse">
              <stop offset="0.103382" stopColor="#AEB2C9" stopOpacity="1" />
              <stop offset="0.524964" stopColor="#DADDE8" stopOpacity="1" />
              <stop offset="0.917701" stopColor="#838AAB" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="paint1_linear_9810_205843" x1="720" y1="636" x2="720" y2="870" gradientUnits="userSpaceOnUse">
              <stop stopColor="#FAFCFF" stopOpacity="1" />
              <stop offset="1" stopColor="#FAFCFF" stopOpacity="1" />
            </linearGradient>
            <clipPath id="clip0_9810_205843">
              <rect width="1440" height="870" fill="white" />
            </clipPath>
            <clipPath id="clip1_9810_205843">
              <rect width="1440" height="870" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </div>

      {/* Header */}
      <header className="w-full px-6 pt-6 pb-0">
        <div className="max-w-[1400px] mx-auto">
          <div
            className="relative 
             bg-white/80 backdrop-blur-md 
             px-8 py-4 rounded-[15px]"
            style={{
              boxShadow: "0 4px 25px 0 rgba(28, 101, 253, 0.08), 0 4px 4px 0 rgba(28, 101, 253, 0.04)",
            }}
          >
            {/* 🔹 СТРОКА (не двигается) */}
            <div className="relative flex items-center justify-between">
              {/* Лого */}
              <Link href="/" className="flex items-center">
                <Image src="/images/logo.webp" alt="Logo" width={180} height={31} priority />
              </Link>

              {/* Десктоп */}
              {/* <nav className="hidden sm:flex items-center gap-8">
                {menu.map((item) => (
                  <Link key={item} href={`/${item === "Home" ? "" : item.toLowerCase()}`} className="text-sm text-gray-600 hover:text-black transition">
                    {item}
                  </Link>
                ))}
              </nav> */}

              <div className="hidden sm:flex items-center gap-8">
                {/* Навигация */}
                <nav className="hidden sm:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
                  {menu.map((item) => (
                    <Link key={item} href={`/${item === "Home" ? "" : item.toLowerCase()}`} className="text-sm text-gray-600 hover:text-black transition">
                      {item}
                    </Link>
                  ))}
                </nav>

                {/* Auth кнопки */}
                <div className="hidden sm:flex items-center gap-3" title={isError ? error?.message ?? "Could not load profile" : undefined}>
                  {!authReady ? (
                    <span className="text-sm text-transparent select-none" aria-hidden>
                      —
                    </span>
                  ) : isLoggedIn ? (
                    <div className="flex items-center gap-2">
                      <Link href={DASHBOARD_HREF} className="text-sm !text-white bg-[#1e7c4c] border border-[#1e7c4c] rounded-md px-3 py-1 hover:bg-[#196f43] transition">
                        Dashboard
                      </Link>
                      <button type="button" onClick={() => void handleSignOut()} className="text-sm text-[#595879] border border-[#e4e2dd] rounded-md px-3 py-1 cursor-pointer hover:bg-[#f4f2ef]">
                        Log out
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setAuthInitialMode("signin");
                          setAuthOpen(true);
                        }}
                        className="text-sm !text-[#595879] border border-[#e4e2dd] rounded-md px-3 py-1 cursor-pointer"
                      >
                        Sign in
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAuthInitialMode("signup");
                          setAuthOpen(true);
                        }}
                        className="text-sm text-white bg-[#1e7c4c] border border-[#1e7c4c] rounded-md px-3 py-1 cursor-pointer hover:bg-[#196f43]"
                      >
                        Create account
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Мобильное меню */}
              <div className="sm:hidden flex items-center">
                <div className="h-6 w-px bg-gray-200 mr-3"></div>

                {/* 👇 текст меняется */}
                <span className="text-xs font-medium text-[#596379]">{isOpen ? "Close" : "Menu"}</span>

                {/* 👇 кнопка */}
                <button onClick={() => setIsOpen(!isOpen)} className="flex flex-col justify-between w-4 h-3 ml-2">
                  <span className={`block h-[1px] w-full bg-gray-600 transition ${isOpen ? "rotate-45 translate-y-1.5" : ""}`} />
                  <span className={`block h-[1px] w-full bg-gray-600 transition ${isOpen ? "opacity-0" : ""}`} />
                  <span className={`block h-[1px] w-full bg-gray-600 transition ${isOpen ? "-rotate-45 -translate-y-1.5" : ""}`} />
                </button>
              </div>
            </div>

            {/* 🔻 ВЫПАДАЮЩЕЕ МЕНЮ (отдельно!) */}
            {isOpen && (
              <nav className="sm:hidden mt-4 flex flex-col gap-1">
                {menu.map((item) => (
                  <Link
                    key={item}
                    href={`/${item === "Home" ? "" : item.toLowerCase()}`}
                    className="py-1.5 text-sm font-medium text-[#596379] hover:text-blue-600 transition"
                    onClick={() => setIsOpen(false)}
                  >
                    {item}
                  </Link>
                ))}
                {authReady && isLoggedIn ? (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex flex-col gap-2">
                    <Link href={DASHBOARD_HREF} className="py-1.5 text-sm font-semibold text-[#1e7c4c]" onClick={() => setIsOpen(false)}>
                      Dashboard
                    </Link>
                    <button
                      type="button"
                      className="w-full text-left py-1.5 text-sm font-medium text-[#596379]"
                      onClick={() => {
                        setIsOpen(false);
                        void handleSignOut();
                      }}
                    >
                      Log out
                    </button>
                  </div>
                ) : null}
                {authReady && !isLoggedIn ? (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex flex-col gap-2">
                    <button
                      type="button"
                      className="w-full text-left py-1.5 text-sm font-medium text-[#596379]"
                      onClick={() => {
                        setIsOpen(false);
                        setAuthInitialMode("signin");
                        setAuthOpen(true);
                      }}
                    >
                      Sign in
                    </button>
                    <button
                      type="button"
                      className="w-full text-left py-1.5 text-sm font-semibold text-[#1e7c4c]"
                      onClick={() => {
                        setIsOpen(false);
                        setAuthInitialMode("signup");
                        setAuthOpen(true);
                      }}
                    >
                      Create account
                    </button>
                  </div>
                ) : null}
              </nav>
            )}
          </div>
        </div>
      </header>
      <AuthPopup
        isOpen={authOpen}
        initialMode={authInitialMode}
        onClose={() => {
          setAuthOpen(false);
          setPostLoginRedirect(null);
          setAuthInitialMode("signin");
        }}
        postLoginRedirect={postLoginRedirect}
      />
    </div>
  );
}

export default function Header() {
  return (
    <Suspense fallback={null}>
      <HeaderImpl />
    </Suspense>
  );
}
